// Vine Runner's rules, kept apart from the drawing so the game, the bot's
// look-ahead and the tests all run the same physics. Heights are altitudes in
// pixels above the vine (up is positive), x is screen pixels.

export type ThingKind =
  | "bar"
  | "stack"
  | "hanging"
  | "pillar"
  | "crawler"
  | "spiker"
  | "hopper"
  | "flyer"
  | "glider"
  | "shooter"
  | "shot"
  | "wave"
  | "bomb"
  | "fire"
  | "beam"
  | "boss";

export interface Thing {
  kind: ThingKind;
  // Left edge for boxes, centre for round things.
  x: number;
  w: number;
  // Bottom altitude for boxes, centre altitude for round things.
  alt: number;
  h: number;
  // Radius of a round thing.
  size: number;
  // Extra speed toward the player on top of the vine's scroll.
  vx: number;
  // Screen fixed things (the boss, its beams and shockwaves) ignore the scroll.
  anchored: boolean;
  // Falling speed (bombs, the boss lowering itself).
  vAlt: number;
  // Seconds before vx starts to apply (the boss lands before it charges).
  delay: number;
  age: number;
  // Sine motion: flyers bob, pillars pump, hoppers hop.
  amp: number;
  omega: number;
  phase: number;
  // Beams are live only between these ages.
  on: number;
  off: number;
  // Seconds left of the death animation. A dying thing no longer hurts.
  dying: number;
  // Hurt the player already, or was stomped or smashed.
  spent: boolean;
  // Cleared by the player, for the combo.
  passed: boolean;
  // Shooters: beats until the next shot.
  reload: number;
  squash: number;
}

export interface Pit {
  x: number;
  w: number;
  passed: boolean;
}

export type PickupKind = "note" | "shield" | "heart";

export interface Pickup {
  kind: PickupKind;
  x: number;
  alt: number;
  taken: boolean;
  phase: number;
}

export interface Box {
  on: boolean;
  round: boolean;
  x0: number;
  x1: number;
  a0: number;
  a1: number;
  cx: number;
  ca: number;
  rad: number;
}

export interface Metrics {
  width: number;
  height: number;
  r: number;
  // Height of a single jump, from the vine to the bottom of the record.
  hj: number;
  g: number;
  v0: number;
  px: number;
  ground: number;
  // Speeds are measured in this.
  unit: number;
  // A phone held upright, framed by its height rather than its width.
  portrait: boolean;
  // Where the floor grid meets the sky.
  horizon: number;
  // The top of the scenery, clear of the score and buttons.
  skyTop: number;
}

export const AIRTIME = 0.74;
// How fast the vine's speed eases toward its target (per second).
export const SCROLL_EASE = 2.5;

// How far the vine scrolls in `tau` seconds while its speed eases from
// `scroll` toward `target`.
export const scrollShift = (scroll: number, target: number, tau: number) => target * tau + ((scroll - target) * (1 - Math.exp(-SCROLL_EASE * tau))) / SCROLL_EASE;
export const DOUBLE_JUMP = 0.85;
export const STOMP_BOUNCE = 0.92;
export const PIT_RESCUE = 1.15;
export const COYOTE = 0.08;
// Hurt boxes are a little smaller than the record so grazes do not count.
export const HURT_SHRINK = 0.78;

export const STOMPABLE: Record<ThingKind, boolean> = {
  bar: false,
  stack: false,
  hanging: false,
  pillar: false,
  crawler: true,
  spiker: false,
  hopper: true,
  flyer: true,
  glider: true,
  shooter: true,
  shot: false,
  wave: false,
  bomb: false,
  fire: false,
  beam: false,
  boss: true,
};

export const isStandable = (kind: ThingKind) => kind === "bar" || kind === "stack";

export function makeThing(kind: ThingKind, x: number, w: number, alt: number, h: number): Thing {
  return { kind, x, w, alt, h, size: 0, vx: 0, anchored: false, vAlt: 0, delay: 0, age: 0, amp: 0, omega: 0, phase: 0, on: 0, off: 0, dying: 0, spent: false, passed: false, reload: 0, squash: 0 };
}

// The vine's speed at the start of level 1, as a share of `unit`.
export const LEVEL_ONE_SPEED = 0.5;
// How fast the vine runs on a phone held upright, in records (radii) per
// second at the start of level 1. It is what the game always ran at upright,
// so the rules in seconds and records do not change with the framing.
const PORTRAIT_PACE = 12.5;
// Records across the width of a phone held upright.
const PORTRAIT_SPAN = 18;
// Seconds of warning upright: a narrow screen shows each obstacle for about
// 1.2s, so the edge strip announces it from here on. A 1280x720 screen shows
// things about 1.85s ahead.
export const PORTRAIT_WARNING = 2;
// The score, combo and buttons sit over the top of the stage.
const HUD_CLEAR = 96;

// Landscape keeps the sizes it always had. A phone held upright is framed by
// its height: bigger records, the vine low and the horizon high, at the same
// pace in records per second as before, with an edge strip for early warning.
export function makeMetrics(width: number, height: number): Metrics {
  const portrait = height > width * 1.2;
  if (!portrait) {
    const r = Math.max(12, Math.min(26, Math.min(width, height) * 0.04));
    const hj = r * 7;
    return {
      width,
      height,
      r,
      hj,
      g: (8 * hj) / (AIRTIME * AIRTIME),
      v0: (4 * hj) / AIRTIME,
      px: Math.max(r * 3, width * 0.2),
      ground: height * 0.84,
      unit: Math.min(width, height * 1.5),
      portrait,
      horizon: height * 0.6,
      skyTop: 0,
    };
  }
  // A short stage (a phone in the page rather than full screen) keeps a double
  // jump and the boss clear of the score.
  const r = Math.max(12, Math.min(30, width / PORTRAIT_SPAN, height / 24));
  const hj = r * 7;
  const ground = height - Math.max(r * 3.4, height * 0.14);
  const skyTop = Math.min(HUD_CLEAR, height * 0.16);
  // The horizon sits just above halfway down, so the scenery fills the sky
  // above the jumps instead of hiding down behind the vine.
  const horizon = Math.min(ground - r * 5, skyTop + (ground - skyTop) * 0.55);
  return {
    width,
    height,
    r,
    hj,
    g: (8 * hj) / (AIRTIME * AIRTIME),
    v0: (4 * hj) / AIRTIME,
    px: r * 2.2,
    ground,
    unit: (r * PORTRAIT_PACE) / LEVEL_ONE_SPEED,
    portrait,
    horizon,
    skyTop,
  };
}

// Seconds until something whose front edge is at `left`, closing at `speed`,
// reaches the record.
export const arrivalIn = (m: Metrics, left: number, speed: number) => (left - m.px - m.r) / Math.max(1, speed);

// Still off the right edge but close enough to show on the warning strip.
export const announced = (m: Metrics, left: number, speed: number) => m.portrait && left >= m.width && arrivalIn(m, left, speed) <= PORTRAIT_WARNING;

export const emptyBox = (): Box => ({ on: false, round: false, x0: 0, x1: 0, a0: 0, a1: 0, cx: 0, ca: 0, rad: 0 });

// Where a thing will be `tau` seconds from now, given how far the vine will
// have scrolled by then (`shift`). The game calls it with tau 0; the bot looks
// ahead with it.
export function thingBox(e: Thing, tau: number, shift: number, out: Box): boolean {
  out.on = e.dying <= 0 && !e.spent;
  const t = e.age + tau;
  const dx = (e.anchored ? 0 : shift) + e.vx * Math.max(0, tau - e.delay);
  const x = e.x - dx;
  switch (e.kind) {
    case "flyer":
    case "glider":
    case "shot": {
      out.round = true;
      out.cx = x;
      out.ca = e.alt + (e.amp ? e.amp * Math.sin(e.omega * t + e.phase) : 0);
      out.rad = e.size;
      break;
    }
    case "bomb": {
      const ca = e.alt - e.vAlt * tau;
      if (ca - e.size > 0) {
        out.round = true;
        out.cx = x;
        out.ca = ca;
        out.rad = e.size;
      } else {
        // Landed: a patch of fire on the vine.
        out.round = false;
        out.x0 = x - e.w / 2;
        out.x1 = x + e.w / 2;
        out.a0 = 0;
        out.a1 = e.h;
        // Fire burns out; e.off is its life after landing.
        const landedFor = tau - Math.max(0, (e.alt - e.size) / Math.max(1, e.vAlt));
        if (landedFor > e.off) out.on = false;
      }
      break;
    }
    case "pillar": {
      out.round = false;
      out.x0 = x;
      out.x1 = x + e.w;
      out.a0 = 0;
      out.a1 = e.h - e.amp * (0.5 + 0.5 * Math.cos(e.omega * t + e.phase));
      break;
    }
    case "hopper": {
      out.round = false;
      out.x0 = x;
      out.x1 = x + e.w;
      out.a0 = e.amp * Math.abs(Math.sin(e.omega * t + e.phase));
      out.a1 = out.a0 + e.h;
      break;
    }
    case "beam": {
      out.round = false;
      out.x0 = x;
      out.x1 = x + e.w;
      out.a0 = e.alt;
      out.a1 = e.alt + e.h;
      if (t < e.on || t > e.off) out.on = false;
      break;
    }
    case "boss": {
      out.round = false;
      out.x0 = x;
      out.x1 = x + e.w;
      out.a0 = Math.max(0, e.alt - e.vAlt * tau);
      out.a1 = out.a0 + e.h;
      break;
    }
    case "fire": {
      out.round = false;
      out.x0 = x;
      out.x1 = x + e.w;
      out.a0 = 0;
      out.a1 = e.h;
      if (t > e.off) out.on = false;
      break;
    }
    case "hanging": {
      out.round = false;
      out.x0 = x;
      out.x1 = x + e.w;
      out.a0 = e.alt;
      out.a1 = e.alt + e.h;
      break;
    }
    default: {
      out.round = false;
      out.x0 = x;
      out.x1 = x + e.w;
      out.a0 = e.alt;
      out.a1 = e.alt + e.h;
    }
  }
  return out.on;
}

export const boxTop = (box: Box) => (box.round ? box.ca + box.rad : box.a1);
export const boxLeft = (box: Box) => (box.round ? box.cx - box.rad : box.x0);
export const boxRight = (box: Box) => (box.round ? box.cx + box.rad : box.x1);

export function overlaps(box: Box, px: number, alt: number, radius: number) {
  if (box.round) {
    const reach = radius + box.rad;
    const dx = px - box.cx;
    const da = alt - box.ca;
    return dx * dx + da * da < reach * reach;
  }
  const nx = px < box.x0 ? box.x0 : px > box.x1 ? box.x1 : px;
  const na = alt < box.a0 ? box.a0 : alt > box.a1 ? box.a1 : alt;
  const dx = px - nx;
  const da = alt - na;
  return dx * dx + da * da < radius * radius;
}

export const CONTACT_NONE = 0;
export const CONTACT_STOMP = 1;
export const CONTACT_HURT = 2;

// Landing on something from above defeats it if it can be stomped; any other
// touch hurts. `margin` grows the hurt box and `slack` is how far below the
// top the record may already be and still count as landing on it. The bot
// plans with a bigger margin and less slack than the game allows.
export function contact(kind: ThingKind, box: Box, px: number, alt: number, prevAlt: number, vUp: number, r: number, margin = 0, slack = 0.55) {
  if (!box.on) return CONTACT_NONE;
  if (!overlaps(box, px, alt, r + margin)) return CONTACT_NONE;
  if (STOMPABLE[kind] && vUp < 0 && prevAlt - r >= boxTop(box) - r * slack && overlaps(box, px, alt, r)) return CONTACT_STOMP;
  if (isStandable(kind) && alt - r >= box.a1 - 1) return CONTACT_NONE;
  if (overlaps(box, px, alt, r * HURT_SHRINK + margin)) return CONTACT_HURT;
  return CONTACT_NONE;
}

export interface Runner {
  alt: number;
  vUp: number;
  jumps: number;
  grounded: boolean;
  air: number;
}

export const copyRunner = (from: Runner, to: Runner) => {
  to.alt = from.alt;
  to.vUp = from.vUp;
  to.jumps = from.jumps;
  to.grounded = from.grounded;
  to.air = from.air;
  return to;
};

// One physics step. `tops` are the tops of standable bars under the record;
// `groundOk` is false over a pit. Returns 1 or 2 when a jump or double jump
// fired. Gravity is integrated exactly so any frame rate lands the same arc.
// `assist` lets a record that has just dipped below a ledge still catch it;
// the game is generous with it and the bot plans without it.
export function stepRunner(p: Runner, dt: number, m: Metrics, jump: boolean, tops: ArrayLike<number>, topCount: number, groundOk: boolean, assist = 1) {
  let jumped = 0;
  if (jump) {
    if (p.grounded || (p.jumps === 0 && p.air < COYOTE)) {
      p.vUp = m.v0;
      p.jumps = 1;
      jumped = 1;
    } else if (p.jumps < 2) {
      p.vUp = m.v0 * DOUBLE_JUMP;
      p.jumps = 2;
      jumped = 2;
    }
    if (jumped) p.grounded = false;
  }
  const prevBottom = p.alt - m.r;
  p.alt += p.vUp * dt - 0.5 * m.g * dt * dt;
  p.vUp -= m.g * dt;
  const bottom = p.alt - m.r;
  let surface = -Infinity;
  if (groundOk && prevBottom >= -assist) surface = 0;
  for (let i = 0; i < topCount; i++) {
    const top = tops[i];
    if (top <= prevBottom + assist && top > surface) surface = top;
  }
  if (p.vUp <= 0 && bottom <= surface) {
    p.alt = surface + m.r;
    p.vUp = 0;
    p.jumps = 0;
    p.grounded = true;
    p.air = 0;
  } else {
    p.grounded = false;
    p.air += dt;
  }
  return jumped;
}

// Standable tops under the record, and whether the vine is there at all.
// `edge` is how far past a bar's end the record can still stand on it; the bot
// plans with a smaller one than the game uses, so it never counts on a lucky
// corner landing.
export function supportAt(px: number, r: number, boxes: Box[], kinds: ThingKind[], count: number, pits: Pit[], pitShift: number, tops: Float64Array, edge = 0.55) {
  let topCount = 0;
  for (let i = 0; i < count; i++) {
    const box = boxes[i];
    if (!box.on || box.round || !isStandable(kinds[i])) continue;
    if (px + r * edge > box.x0 && px - r * edge < box.x1 && topCount < tops.length) tops[topCount++] = box.a1;
  }
  let groundOk = true;
  for (const pit of pits) {
    const x = pit.x - pitShift;
    if (px > x + r * 0.35 && px < x + pit.w - r * 0.35) {
      groundOk = false;
      break;
    }
  }
  return { topCount, groundOk };
}

// A record that fell this far into a pit has fallen in.
export const fellIn = (p: Runner, m: Metrics) => p.alt < -m.r * 0.6;
